import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    label = "label",
    isRequired = "isRequired",
    columns = "columns",
    topics = "topics",
}

interface TopicModel {
    fields: {
        key: string;
        label: string;
    };
}

interface OwnProps {
    onGroupChangeHandler?: (newValue: string) => void;
    fields: {
        [fields.key]: string;
        [fields.label]: string;
        [fields.isRequired]: boolean;
        [fields.columns]: number;
        [fields.topics]: TopicModel[];
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms; // real-time view into the state
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormRadioGroupStyles {
    wrapper?: InjectableCss;
    labelWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    error?: TypographyPresentationProps;
}

export const dynamicFormRadioGroupStyles: DynamicFormRadioGroupStyles = {
    wrapper: {
        css: css`
            width: 100%;
            margin-top: 0.5em;
            margin-bottom: 1em;
        `,
    },
    labelWrapper: {
        css: css`
            width: 100%;
        `,
    },
    label: {
        weight: 600,
    },
    errorWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    error: {
        css: css`
            color: #e64e25;
            font-size: 15px;
            font-weight: 600;
        `,
    },
};

export const styles = dynamicFormRadioGroupStyles;

type DynamicFormRadioGroupValidator = {
    fieldKey: string;
    validate: () => { isValid: boolean; errorMessage: string };
};

type DynamicFormRadioGroupContextType = {
    validators: DynamicFormRadioGroupValidator[];
};

export const DynamicFormRadioGroupContext = React.createContext<DynamicFormRadioGroupContextType>({
    validators: [] as DynamicFormRadioGroupValidator[],
});
// Functional Component (FC), class, and function are similar but have different definitions
const DynamicFormRadioGroup: React.FC<Props> = ({
    onGroupChangeHandler,
    fields,
    dynamicFormData,
    setDynamicFormFieldValue,
}) => {
    const { key, label, isRequired, columns, topics } = fields;
    const defaultRadioValue = "";
    const [radioGroupErrorMessage, setRadioGroupErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);
    const [radioVal, setRadioVal] = React.useState(defaultRadioValue);

    const [validators] = useState<DynamicFormRadioGroupValidator[]>([]);

    const dynamicFormContext = useContext(DynamicFormContext);

    useEffect(() => {
        initializeWidget();
    }, [dynamicFormData.forms]);

    useEffect(() => {
        if (radioVal !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setRadioVal(defaultRadioValue);
            } else {
                setRadioVal(dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    useEffect(() => {
        const { isValid, errorMessage } = validateRadioGroupLocal();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label,
                isRequired,
                value: radioVal,
                type: "DynamicFormRadioGroup",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    }, [radioVal]);

    const initializeWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = validateRadioGroupLocal();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "rbGroup",
                label,
                isRequired,
                value: "",
                type: "DynamicFormRadioGroup",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    };

    const validateRadioGroupLocal = () => {
        let errorMessage = "";
        const value = radioVal;

        if (isRequired) {
            if (value === "") {
                errorMessage = siteMessage("DynamicForms_Require_One_Item_Checked") as string;
            }
        }

        setRadioGroupErrorMessage(errorMessage);

        return {
            isValid: !errorMessage,
            errorMessage,
        };
    };

    const getDisplayValidationErrors = () => {
        const formIndex = determineFormIndex(dynamicFormContext.formName, dynamicFormData).formIndex;
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    const groupChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRadioVal(e.target.value);

        if (onGroupChangeHandler) {
            onGroupChangeHandler(e.target.value);
        }
    };

    const columnElements: JSX.Element[] = [];
    for (let x = 0; x < topics.length; x += 1) {
        columnElements.push(
            <Radio key={x} value={topics[x].fields.key}>
                {topics[x].fields.label}
            </Radio>,
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.labelWrapper}>
                    <Typography {...styles.label}>{isRequired ? `${label  } *` : label}</Typography>
                </StyledWrapper>
            <DynamicFormRadioGroupContext.Provider value={{ validators }}>
                <RadioGroup
                    value={radioVal}
                    onChangeHandler={e => {
                        groupChangeHandler(e);
                    }}
                >
                    {columnElements}
                </RadioGroup>
            </DynamicFormRadioGroupContext.Provider>
            {getDisplayValidationErrors() && radioGroupErrorMessage && (
                <StyledWrapper {...styles.errorWrapper}>
                    <Typography {...styles.error}>{radioGroupErrorMessage || isRequired}</Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormRadioGroup);
